<template>
  <transition v-if="isSignup" name="appear">
    <LoginSignupContainer>
      <div class="create-entity-container">
        <div>
          <FriedParagraph class="branding">{{ $t('create-account') }}</FriedParagraph>
          <FriedH2 class="branding create-entity-title">{{ $t('create-company-profile') }}</FriedH2>
        </div>
        <FriedMessage v-if="error" class="error" :title="$t(error.title)" :type="MessageType.Error">
          <i18n-t :keypath="error.description">
            <template #contactSupportLink>
              <FriedLink :normal="true" href="http://help.getaccept.com/" target="_blank">
                {{ $t('contact-support') }}
              </FriedLink>
            </template>
          </i18n-t>
        </FriedMessage>
        <CreateEntityForm
          v-if="initialFormData"
          id="createEntityForm"
          :initial-form-data="initialFormData"
          @submit="handleSubmit"
        />
        <FriedButton
          type="submit"
          form="createEntityForm"
          class="create-entity-button"
          data-external="create-entity-submit-button"
          :loading="loading"
        >
          {{ $t('continue') }}
        </FriedButton>
      </div>
    </LoginSignupContainer>
  </transition>
  <div v-else>
    <div class="create-entity-background" />
    <FriedModal class="create-entity-modal" @close="handleCancel">
      <template #header>
        <FriedModalHeader
          :header="$t('create-company-profile')"
          :description="$t('enter-the-name-of-your-company-or-organization')"
        />
      </template>
      <FriedMessage v-if="error" class="error" :title="$t(error.title)" :type="MessageType.Error">
        <i18n-t :keypath="error.description">
          <template #contactSupportLink>
            <FriedLink :normal="true" href="http://help.getaccept.com/" target="_blank">
              {{ $t('contact-support') }}
            </FriedLink>
          </template>
        </i18n-t>
      </FriedMessage>
      <CreateEntityForm
        v-if="initialFormData"
        id="createEntityForm"
        :initial-form-data="initialFormData"
        @submit="handleSubmit"
      />
      <template #footer>
        <FriedModalButtonsFooter>
          <FriedButton
            type="button"
            :button-type="ButtonType.Secondary"
            data-external="create-entity-cancel-button"
            @click="handleCancel"
          >
            {{ $t('cancel') }}
          </FriedButton>
          <FriedButton
            type="submit"
            form="createEntityForm"
            data-external="create-entity-submit-button"
            :loading="loading"
          >
            {{ $t('continue') }}
          </FriedButton>
        </FriedModalButtonsFooter>
      </template>
    </FriedModal>
  </div>
</template>

<script lang="ts">
import { ButtonType, MessageType } from '@getaccept/fried-tofu';
import type { Ref } from 'vue';
import { defineComponent, onMounted, ref } from 'vue';
import LoginSignupContainer from '@getaccept/lib-shared-new/src/login-signup/components/LoginSignupContainer.vue';
import type { CreateEntityFormData } from '../types/create-entity-form-data';
import type { ErrorMessage } from '../../types/error-message';
import { CreateEntityHelper } from '../../helpers/create-entity.helper';
import { TimezoneHelper } from '../../helpers/timezone.helper';
import { DatacenterHelper } from '../../helpers/datacenter.helper';
import CreateEntityForm from './CreateEntityForm.vue';

export default defineComponent({
  name: 'CreateEntity',
  components: {
    CreateEntityForm,
    LoginSignupContainer,
  },
  props: {
    isSignup: { type: Boolean, default: false },
    userId: { type: String, default: '' },
    entityName: { type: String, default: '' },
    entityCountryCode: { type: String, default: '' },
  },
  setup(props) {
    const loading = ref(false);
    const error: Ref<ErrorMessage> = ref(null);
    const initialFormData: Ref<CreateEntityFormData> = ref(null);

    const handleCancel = () => {
      window.location.href = '/dashboard';
    };

    const handleSubmit = async (formData: CreateEntityFormData) => {
      try {
        loading.value = true;
        await CreateEntityHelper.createEntity(formData);
        CreateEntityHelper.trackSignupSuccess(props.isSignup, props.userId, formData);
      } catch (e) {
        error.value = e;
        CreateEntityHelper.trackSignupError(error.value);
      } finally {
        loading.value = false;
      }
    };

    const setInitialFormData = async () => {
      const geoLocation = await CreateEntityHelper.getGeoLocation();
      const timezone = TimezoneHelper.getTimezoneAsString();
      const countryCode = props.entityCountryCode || geoLocation.country_code || 'US';
      const datacenter = DatacenterHelper.getDatacenterFromCountry(countryCode).id;

      initialFormData.value = {
        name: props.entityName || '',
        countryCode,
        timezone,
        datacenter,
      };
    };

    onMounted(() => {
      setInitialFormData();
    });

    return {
      ButtonType,
      MessageType,
      loading,
      error,
      initialFormData,
      setInitialFormData,
      handleCancel,
      handleSubmit,
    };
  },
});
</script>

<style lang="scss" scoped>
.create-entity-background {
  filter: blur(8px);
  background: url('../assets/fake-app-background.png') top left no-repeat var(--gray-bg);
  background-size: 100% auto;
  height: 100vh;

  @media only screen and (max-width: $sm) {
    background: url('../assets/fake-app-mobile-background.png') top left no-repeat var(--gray-bg);
    background-size: 100% auto;
  }
}

.create-entity-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-150);

  .create-entity-button {
    width: 100%;
  }

  .error {
    margin-bottom: var(--spacing-50);
  }
}
</style>
